/**
 * IntakeQuestionCard — displays a single intake interview question
 * with selectable option cards.
 *
 * Supports both single-select (radio group) and multi-select (checkbox group)
 * modes based on the question's multiSelect property.
 */

import { cn } from "../../utils/cn";
import type { IntakeQuestion } from "../../types/intake";

interface IntakeQuestionCardProps {
  /** Question definition from INTAKE_QUESTIONS */
  question: IntakeQuestion;
  /** Currently selected values */
  selectedValues: string[];
  /** Callback when selection changes */
  onSelect: (values: string[]) => void;
}

export default function IntakeQuestionCard({
  question,
  selectedValues,
  onSelect,
}: IntakeQuestionCardProps) {
  const isMultiSelect = question.multiSelect;

  const handleOptionClick = (value: string) => {
    if (isMultiSelect) {
      handleMultiSelect(value);
    } else {
      onSelect([value]);
    }
  };

  const handleMultiSelect = (value: string) => {
    // Exclusive options: selecting "debt-free" or "none" clears others
    const exclusiveValues = ["debt-free", "none"];
    if (exclusiveValues.includes(value)) {
      onSelect([value]);
      return;
    }

    // Deselect exclusive values when selecting a regular option
    const withoutExclusive = selectedValues.filter(
      (v) => !exclusiveValues.includes(v)
    );

    // Toggle the clicked value
    if (withoutExclusive.includes(value)) {
      onSelect(withoutExclusive.filter((v) => v !== value));
    } else {
      onSelect([...withoutExclusive, value]);
    }
  };

  const groupRole = isMultiSelect ? "group" : "radiogroup";

  return (
    <div className="intake-question-card" id={`intake-question-${question.id}`}>
      <h2 className="intake-question-text">{question.question}</h2>
      {question.helpText && (
        <p className="intake-question-help">{question.helpText}</p>
      )}

      <div
        role={groupRole}
        aria-label={question.question}
        className="intake-options"
      >
        {question.options.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const role = isMultiSelect ? "checkbox" : "radio";

          return (
            <button
              key={option.value}
              type="button"
              role={role}
              aria-checked={isSelected}
              className={cn(
                "intake-option",
                isSelected && "intake-option-selected"
              )}
              onClick={() => handleOptionClick(option.value)}
            >
              <span className="intake-option-icon" aria-hidden="true">
                {option.icon}
              </span>
              <span className="intake-option-content">
                <span className="intake-option-label">{option.label}</span>
                {option.description && (
                  <span className="intake-option-description">
                    {option.description}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
